import React, { useEffect, useMemo, useRef, useState } from "react";

import { CircularIndeterminate, FormButton } from "components";
import { FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import {
    rowSelected,
    snackbarData,
    userCredentials,
} from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import { migraPasoUpd, migraTipoDePasoGet } from "services/services-mia/migra";
import { accesoGet } from "services/services-mia/miakey";

function AutoNombre({ lugares }) {
    const { values, setFieldValue } = useFormikContext();
    const prevLugarNombreRef = useRef(null);
    const nombreRef = useRef(values.migraPasoNombre);
    nombreRef.current = values.migraPasoNombre;

    useEffect(() => {
        const lugar = lugares.find(
            (l) => String(l.gateLugarId) === String(values.gateLugarId)
        );
        if (!lugar) return;
        const shouldUpdate =
            !nombreRef.current ||
            nombreRef.current === prevLugarNombreRef.current;
        if (shouldUpdate) setFieldValue("migraPasoNombre", lugar.gateLugarNombre);
        prevLugarNombreRef.current = lugar.gateLugarNombre;
    }, [values.gateLugarId, lugares, setFieldValue]);
    return null;
}

export default function UpdateModal({ updateList, paso, closeModal }) {
    const setSelected = useSetRecoilState(rowSelected);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const [inRequest, setInRequest] = useState(false);
    const [loadingOpts, setLoadingOpts] = useState(true);
    const [lugares, setLugares] = useState([]);
    const [tiposPaso, setTiposPaso] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingOpts(true);
            try {
                const [lugRes, tiposRes] = await Promise.all([
                    accesoGet({ incluirLugaresGlobales: "N" }, credentials),
                    migraTipoDePasoGet({}, credentials),
                ]);
                if (!cancelled) {
                    setLugares(lugRes.result || []);
                    setTiposPaso(tiposRes.result || []);
                }
            } finally {
                if (!cancelled) setLoadingOpts(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [credentials]);

    const lugaresOpciones = useMemo(() => {
        const base = [...lugares];
        if (
            paso?.gateLugarId != null &&
            !base.some((l) => l.gateLugarId === paso.gateLugarId)
        ) {
            base.push({
                gateLugarId: paso.gateLugarId,
                gateLugarNombre:
                    paso.gateLugarNombre || `Lugar #${paso.gateLugarId}`,
            });
        }
        return base;
    }, [lugares, paso]);

    const handleSubmit = async ({
        migraPasoCod,
        migraPasoNombre,
        gateLugarId,
        migraTipoDePasoId,
    }) => {
        setInRequest(true);

        const cod =
            typeof migraPasoCod === "string"
                ? migraPasoCod.trim() || null
                : null;

        const nombre =
            typeof migraPasoNombre === "string"
                ? migraPasoNombre.trim() || null
                : null;

        const response = await migraPasoUpd(
            {
                migraPasoId: paso.id,
                migraPasoCod: cod,
                migraPasoNombre: nombre,
                gateLugarId: Number(gateLugarId),
                migraTipoDePasoId: Number(migraTipoDePasoId),
                migraPasoVersion: paso.migraPasoVersion,
            },
            credentials
        );

        handleResponse(response, "Paso modificado exitosamente", {
            setSnackbarInfo,
            setIsOpen: closeModal,
            setSelected,
            updateList,
        });

        setInRequest(false);
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{
                migraPasoCod: paso.migraPasoCod ?? "",
                migraPasoNombre: paso.migraPasoNombre ?? "",
                gateLugarId: paso.gateLugarId ?? "",
                migraTipoDePasoId: paso.migraTipoDePasoId ?? "",
            }}
            validationSchema={yup.object({
                migraPasoCod: yup
                    .string(FORM_ERROR_MESSAGES.text)
                    .trim()
                    .max(50, "Máximo 50 caracteres")
                    .nullable(),
                migraPasoNombre: yup
                    .string(FORM_ERROR_MESSAGES.text)
                    .trim()
                    .max(150, "Máximo 150 caracteres")
                    .nullable(),
                gateLugarId: yup
                    .mixed()
                    .required(FORM_ERROR_MESSAGES.required)
                    .test(
                        "num",
                        FORM_ERROR_MESSAGES.number,
                        (v) => v !== "" && v != null && !Number.isNaN(Number(v))
                    ),
                migraTipoDePasoId: yup
                    .mixed()
                    .required(FORM_ERROR_MESSAGES.required)
                    .test(
                        "num",
                        FORM_ERROR_MESSAGES.number,
                        (v) => v !== "" && v != null && !Number.isNaN(Number(v))
                    ),
            })}
            onSubmit={(values, { setSubmitting }) => {
                try {
                    handleSubmit(values);
                } catch (e) {
                    setSnackbarInfo({
                        message: e.errmsg,
                        severity: "error",
                        open: true,
                    });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            <Form className={styles.inputs} noValidate>
                <AutoNombre lugares={lugaresOpciones} />
                <div className={styles["column-sm"]}>
                    {loadingOpts ? (
                        <CircularIndeterminate />
                    ) : (
                        <>
                            <FormikTextInput
                                fullWidth={true}
                                name="migraPasoNombre"
                                labelText="Nombre"
                            />
                            <FormikTextInput
                                fullWidth={true}
                                name="migraPasoCod"
                                labelText="Código"
                            />
                            <FormikSelectInput
                                name="gateLugarId"
                                labelText="Lugar"
                                disabled={lugaresOpciones.length === 0}
                            >
                                {lugaresOpciones.map(
                                    ({
                                        gateLugarId,
                                        gateLugarNombre,
                                    }) => (
                                        <option
                                            key={gateLugarId}
                                            value={gateLugarId}
                                        >
                                            {gateLugarNombre}
                                        </option>
                                    )
                                )}
                            </FormikSelectInput>
                            <FormikSelectInput
                                name="migraTipoDePasoId"
                                labelText="Tipo de paso"
                                disabled={tiposPaso.length === 0}
                            >
                                {tiposPaso.length === 0 ? (
                                    <option value="">
                                        No hay tipos de paso
                                    </option>
                                ) : (
                                    tiposPaso.map(
                                        ({
                                            migraTipoDePasoId,
                                            migraTipoDePasoNombre,
                                        }) => (
                                            <option
                                                key={migraTipoDePasoId}
                                                value={migraTipoDePasoId}
                                            >
                                                {migraTipoDePasoNombre}
                                            </option>
                                        )
                                    )
                                )}
                            </FormikSelectInput>
                            <FormButton
                                inRequest={inRequest}
                                newFormData={{ button: "Guardar cambios" }}
                                onCloseModal={closeModal}
                            />
                        </>
                    )}
                </div>
            </Form>
        </Formik>
    );
}
